import { type ReactNode, useCallback, useId, useMemo } from 'react';

import {
  CopyToClipboardButton,
  FormField,
  type RequireLabelOrAria,
  getLabelOrAriaProps,
  isDefined,
} from 'common';

import { MemoizedBlockValueTemplateInnerField } from './MemoizedFlowTemplate';
import {
  type CodeMirrorVariant,
  getCodeMirrorFormFieldSxProps,
  getCodeMirrorStyles,
} from './defaultProps';
import { useCodeMirrorAutocompleteContext } from '../../../../Code/codeMirrorHooks';
import { useReactFlowViewContext } from '../../../context/context';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

type FieldProps = ConfigFieldProps & {
  autoFocus?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  isMultiline?: boolean;
  minHeight?: string;
  maxHeight?: string;
  onUpdate?: (value: string) => void;
  fallbackValue?: string;
} & RequireLabelOrAria & { labelAdornment?: ReactNode };

type InnerFieldProps = FieldProps & {
  fieldId: string;
  variant?: CodeMirrorVariant;
};

export function BlockValueTemplateInnerField(props: InnerFieldProps) {
  const {
    label,
    'aria-label': ariaLabel,
    fieldName,
    autoFocus,
    fieldId,
    variant,
    isMultiline = false,
    minHeight,
    maxHeight,
    placeholder,
    onUpdate,
    fallbackValue,
  } = props;
  const { field, value, isFieldErrorVisible, setValue } = useSelectedNodeField<string>(fieldName);
  const { isEditable } = useReactFlowViewContext();
  const { flowTemplateNodeNameExtensions } = useCodeMirrorAutocompleteContext();

  const wrapperSx = useMemo(() => {
    return getCodeMirrorFormFieldSxProps({
      isEditable,
      editorElementStyles: {
        /**
         * This padding makes the component look like our other small text fields.
         */
        paddingX: '6px',
        paddingY: '4px',
      },
    });
  }, [isEditable]);

  const codeMirrorStyles = useMemo(() => {
    return getCodeMirrorStyles({
      isFieldErrorVisible,
      variant,
    });
  }, [isFieldErrorVisible, variant]);

  const onChange = useCallback(
    async (value: string) => {
      if (isEditable) {
        setValue(value);
        onUpdate?.(value);
      }
    },
    [isEditable, setValue, onUpdate]
  );

  return (
    <MemoizedBlockValueTemplateInnerField
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : 'Value')}
      wrapperSx={wrapperSx}
      codeMirrorStyles={codeMirrorStyles}
      autoFocus={autoFocus}
      isMultiline={isMultiline}
      placeholder={placeholder}
      field={field}
      extensions={flowTemplateNodeNameExtensions}
      isEditable={isEditable}
      value={isDefined(value) ? value : fallbackValue}
      onChange={onChange}
      fieldId={fieldId}
      minHeight={minHeight}
      maxHeight={maxHeight}
      onUpdate={onUpdate}
    />
  );
}

/**
 * Most FlowBuilder blocks should use this field. It supports FlowBuilder's template syntax.
 * Example: `{{block.data.fieldName}}`
 */
export function BlockValueTemplateField(props: FieldProps) {
  const {
    label,
    fieldName,
    'aria-label': ariaLabel,
    labelAdornment,
    helperText,
    fallbackValue,
  } = props;
  const { visibleError, value } = useSelectedNodeField<string>(fieldName);

  const fieldId = useId();

  return (
    <FormField
      id={fieldId}
      {...getLabelOrAriaProps({ label, 'aria-label': ariaLabel })}
      error={visibleError}
      helperText={helperText}
      labelAdornment={
        labelAdornment ?? (
          <CopyToClipboardButton
            animationVariant="AlwaysVisible"
            content={isDefined(value) ? value : (fallbackValue ?? '')}
          />
        )
      }
    >
      <BlockValueTemplateInnerField {...props} fieldId={fieldId} />
    </FormField>
  );
}

export function BlockValueTemplateFieldArea(props: FieldProps) {
  return <BlockValueTemplateField minHeight="120px" {...props} isMultiline />;
}
